/**
 * Calendar Store
 * Zustand store for calendar navigation state.
 * Manages selected date, visible week, navigation actions, and shift filters.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { startOfDay, startOfWeek, addDays, addMonths, isWithinInterval } from 'date-fns';
import { ShiftStatus, TaskStatus } from '@signalvote/shared';
import { getCampaignToday } from '@/lib/calendarUtils';

// ============================================================================
// Types
// ============================================================================

/** Calendar view type */
export type CalendarView = 'week' | 'month';

/** Filter type for shift status - 'All' shows all shifts, or filter by specific status */
export type ShiftStatusFilter = ShiftStatus | 'All';

/** Filter type for task status - 'All' shows all tasks, or filter by specific status */
export type TaskStatusFilter = TaskStatus | 'All';

export interface CalendarState {
  // State
  selectedDate: Date;
  visibleStartDate: Date; // First day of the visible 7-day window (today on initial load)
  currentView: CalendarView; // Active calendar view
  shiftStatusFilter: ShiftStatusFilter; // Filter shifts by status
  taskStatusFilter: TaskStatusFilter; // Filter tasks by status
  activeFilters: string[]; // 'all' | category GUIDs | 'uncategorized' | 'shifts' | 'calendar'
  showDelegated: boolean; // Show tasks created by me and assigned to others

  // Navigation Actions
  setSelectedDate: (date: Date) => void;
  goToToday: () => void;
  navigateWeek: (direction: 'prev' | 'next') => void;
  navigateMonth: (direction: 'prev' | 'next') => void;

  // View Actions
  setView: (view: CalendarView) => void;

  // Filter Actions
  setShiftStatusFilter: (filter: ShiftStatusFilter) => void;
  setTaskStatusFilter: (filter: TaskStatusFilter) => void;
  toggleFilter: (filterId: string) => void;
  resetFilters: () => void;
  setShowDelegated: (value: boolean) => void;

  // Getters (computed via get())
  getVisibleDays: () => Date[];
  getVisibleDayCount: () => number;
  isViewingToday: () => boolean;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Generate an array of 7 days starting from the given date.
 */
function generateWeekDays(startDate: Date): Date[] {
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    days.push(addDays(startDate, i));
  }
  return days;
}

// ============================================================================
// Store
// ============================================================================

const today = getCampaignToday();
const weekStart = startOfWeek(today, { weekStartsOn: 0 });

export const useCalendarStore = create<CalendarState>()(
  devtools(
    (set, get) => ({
      // Initial state: selected date is today, visible window starts at Sunday of current week
      selectedDate: today,
      visibleStartDate: weekStart,
      currentView: 'week' as CalendarView,
      shiftStatusFilter: 'All' as ShiftStatusFilter,
      taskStatusFilter: 'All' as TaskStatusFilter,
      activeFilters: ['all'],
      showDelegated: true,

      // Set selected date and start the 7-day window from the Sunday of that week
      setSelectedDate: (date) =>
        set(
          {
            selectedDate: date,
            visibleStartDate: startOfWeek(startOfDay(date), { weekStartsOn: 0 }),
          },
          undefined,
          'setSelectedDate'
        ),

      // Reset to today (snap to Sunday of current week, campaign timezone)
      goToToday: () => {
        const today = getCampaignToday();
        set(
          {
            selectedDate: today,
            visibleStartDate: startOfWeek(today, { weekStartsOn: 0 }),
          },
          undefined,
          'goToToday'
        );
      },

      // Navigate by week (add/subtract 7 days)
      navigateWeek: (direction) =>
        set(
          (state) => ({
            visibleStartDate: addDays(
              state.visibleStartDate,
              direction === 'next' ? 7 : -7
            ),
          }),
          undefined,
          'navigateWeek'
        ),

      // Navigate by month (add/subtract 1 month)
      navigateMonth: (direction) =>
        set(
          (state) => ({
            visibleStartDate: addMonths(
              state.visibleStartDate,
              direction === 'next' ? 1 : -1
            ),
          }),
          undefined,
          'navigateMonth'
        ),

      // Set calendar view
      setView: (view) => set({ currentView: view }, undefined, 'setView'),

      // Set shift status filter
      setShiftStatusFilter: (filter) =>
        set({ shiftStatusFilter: filter }, undefined, 'setShiftStatusFilter'),

      // Set task status filter
      setTaskStatusFilter: (filter) =>
        set({ taskStatusFilter: filter }, undefined, 'setTaskStatusFilter'),

      // Toggle a category filter chip
      toggleFilter: (filterId) =>
        set(
          (state) => {
            if (filterId === 'all') {
              return { activeFilters: ['all'] };
            }
            const without = state.activeFilters.filter((f) => f !== 'all' && f !== filterId);
            const isActive = state.activeFilters.includes(filterId);
            const next = isActive ? without : [...without, filterId];
            return { activeFilters: next.length === 0 ? ['all'] : next };
          },
          undefined,
          'toggleFilter'
        ),

      // Reset filters to show all
      resetFilters: () => set({ activeFilters: ['all'] }, undefined, 'resetFilters'),

      // Toggle delegated tasks visibility
      setShowDelegated: (value) => set({ showDelegated: value }, undefined, 'setShowDelegated'),

      // Get array of visible days (7 days from visibleStartDate)
      getVisibleDays: () => {
        return generateWeekDays(get().visibleStartDate);
      },

      // Get number of visible days (currently fixed at 7)
      getVisibleDayCount: () => {
        return 7;
      },

      // Check if today (campaign timezone) is within the visible 7-day window
      isViewingToday: () => {
        const today = getCampaignToday();
        const { visibleStartDate } = get();
        const windowEnd = addDays(visibleStartDate, 6);
        return isWithinInterval(today, {
          start: visibleStartDate,
          end: windowEnd,
        });
      },
    }),
    { name: 'calendar-store' }
  )
);
